import { useActionState } from "react";
import { createSkillAction } from "@/actions/skills";
import { Button } from "@/components/Button";
import { CodeCell, FormCell } from "@/components/FormCell";
import styles from "@/components/FormRow.module.css";
import { LinkButton } from "@/components/LinkButton";
import { useCodeFromLabel } from "@/components/useCodeFromLabel";
import { initialFormState } from "@/lib/formState";
import { useFormRedirect } from "@/lib/useFormRedirect";

const FORM_ID = "skill-draft";

type SkillDraftRowProps = {
  // The listing owns the rows, so a save has to tell it to read again.
  onSaved: () => void;
};

export const SkillDraftRow = ({ onSaved }: SkillDraftRowProps) => {
  const [state, formAction, isPending] = useActionState(
    createSkillAction,
    initialFormState,
  );

  useFormRedirect(state, onSaved);

  // The code derives from the label until someone edits it, so the row owns
  // both fields rather than each cell owning its own.
  const { label, code, setLabel, setCode } = useCodeFromLabel(
    state.values.label,
    state.values.code,
  );

  return (
    <tr>
      <CodeCell
        form={FORM_ID}
        name="code"
        label="Code"
        isRowHeader
        placeholder="READING"
        value={code}
        onValueChange={setCode}
        errors={state.fieldErrors.code}
      />
      <FormCell
        form={FORM_ID}
        name="label"
        label="Label"
        placeholder="Reading"
        value={label}
        onValueChange={setLabel}
        errors={state.fieldErrors.label}
      />
      <FormCell
        form={FORM_ID}
        name="description"
        label="Description"
        placeholder="Time spent with books"
        defaultValue={state.values.description}
        errors={state.fieldErrors.description}
      />
      <FormCell
        form={FORM_ID}
        name="icon"
        label="Icon"
        placeholder="book"
        defaultValue={state.values.icon}
        errors={state.fieldErrors.icon}
      />
      <FormCell
        form={FORM_ID}
        name="color"
        label="Color"
        placeholder="#b68235"
        defaultValue={state.values.color}
        errors={state.fieldErrors.color}
      />
      {/* Status, Created and the actions column, none of which a draft has. */}
      <td className={styles.actions} colSpan={3}>
        {/* A form may not be a child of tbody, but it may sit inside a cell —
            the inputs above associate to it by id. */}
        <form id={FORM_ID} action={formAction} hidden />
        <Button
          type="submit"
          form={FORM_ID}
          variant="primary"
          disabled={isPending}
        >
          {isPending ? "Creating..." : "Create"}
        </Button>
        <LinkButton href="/skills" variant="secondary">
          Cancel
        </LinkButton>
      </td>
    </tr>
  );
};
